import CardCommon from '@op/components/CardCommon';
import GPT from '@op/components/GPT';
import { useGetSummaryList } from '@op/components/GPT/hooks/useGetSummary';
import {
  PromptListType,
  PromptConfirmItemType,
  ScenarioType,
} from '@op/components/GPT/types';
import RenderImg from '@op/components/RenderImg';
import { dealwithVideoJSON } from '@op/pages/community/utils';
import { DBO_IMG } from '@op/utils/format';
import React, { useState, useRef, useEffect } from 'react';
import { Row, Col, Button, Icon } from 'tea-component';
import { useIntl } from 'umi';
import { GPTContentType, PromptKeyType } from '../../../types';
import SvgIcon from '@home/components/SvgIcon';

type RenderGptListProps = {
  key: GPTContentType;
  rowClassName?: string;
  pageCardClassName?: string;
  title: any;
  hasAdvancedSummary?: boolean;
  state?: any;
  taskId?: string;
  scenario?: ScenarioType;
  promptList?: PromptListType;
  paramsKey: 'comment_task_id' | 'video_task_id';
}[];

const keyMap: { [key in GPTContentType]: string } = {
  comments: 'comments_task',
  video: 'video_task',
};

const emptyDescList = [
  'Task generation failed, possibly due to the following reasons:',
  '1. The crawler is unable to retrieve the necessary content for a summary from this link',
  '2. Gemini API is unstable',
];

export default function GPTContentInfo({
  gptList,
  filter,
  saveTipsElement,
  promptList,
  setApiLoading,
  promptInfoMap,
  setPromptInfo,
  isPreviews,
  gptTaskList,
  videoPromptList,
}: {
  gptList: GPTContentType[];
  gptTaskList: any;
  filter: any;
  promptList: PromptListType;
  setApiLoading?: (flag: string) => void;
  promptInfoMap: any;
  setPromptInfo: (
    value: PromptConfirmItemType,
    key: PromptKeyType,
    paramsKey: 'video_task_id' | 'comment_task_id',
  ) => Promise<boolean>;
  saveTipsElement: any;
  isPreviews?: boolean;
  videoPromptList: PromptListType;
}) {
  const { formatMessage } = useIntl();
  const [renderGptList, setRenderGptList] = useState<RenderGptListProps>([]);
  const { initRef, getSummary, isFirst, toggleSummaryPrompt } =
    useGetSummaryList<PromptKeyType>({
      isPreviews,
      keys: ['comments', 'video'],
    });
  const gptTaskIdRef = useRef<{
    comments: { task_id: string; error_code: string };
    video: { task_id: string; error_code: string };
  }>({
    comments: { task_id: '', error_code: '' },
    video: { task_id: '', error_code: '' },
  });

  useEffect(() => {
    const list: RenderGptListProps = [
      {
        key: 'video',
        rowClassName: 'dbo-analysis-card-container--video',
        pageCardClassName: 'gpt-video-summary',
        title: 'Video Content Summary',
        hasAdvancedSummary: false,
        scenario: 'url_video_task',
        paramsKey: 'video_task_id',
        promptList: videoPromptList,
      },
      {
        key: 'comments',
        title: 'Comments Summary',
        hasAdvancedSummary: true,
        paramsKey: 'comment_task_id',
        scenario: 'url_comments_task',
        promptList,
      },
    ];

    const render = list.map(({ key, scenario, ...item }) => {
      if (!gptList.includes(key)) return null;

      const realKey = keyMap[key];
      const taskInfo = gptTaskList?.[realKey] || {};
      const taskId = taskInfo.task_id;
      const state = { total: key === 'video' ? 0 : taskInfo.count };

      const promptInfo = promptInfoMap[key];
      gptTaskIdRef.current[key] = {
        task_id: taskId,
        error_code: taskInfo.error_code,
      };

      if (taskInfo.error_code) {
        setApiLoading?.(`${key}_gpt`);
      }

      setTimeout(() => {
        if (taskId && isFirst[key]) {
          getSummary({ ...promptInfo, taskId, scenario }, key);
        }
      }, 100);

      return { ...item, key, state, scenario };
    });

    setRenderGptList(render.filter((item) => item !== null));
  }, [gptList, gptTaskList]);

  const [videoImgInfo, setVideoImgInfo] = useState<{
    loading: boolean;
    leftElement: any;
  }>({
    loading: true,
    leftElement: undefined,
  });

  useEffect(() => {
    const taskInfo = gptTaskList?.video_task;
    if (gptList.includes('video')) {
      const { cover_image } = dealwithVideoJSON(taskInfo?.video_detail);
      if (cover_image) {
        DBO_IMG.judge(cover_image).then((flag) => {
          let leftElement = undefined;
          if (flag) {
            leftElement = {
              span: 9,
              gap: 16,
              element: (
                <RenderImg
                  coverImage={cover_image}
                  style={{ width: '100%', height: 236 }}
                ></RenderImg>
              ),
            };
          }
          setVideoImgInfo({ loading: false, leftElement });
        });
      } else {
        setVideoImgInfo({ loading: false, leftElement: null });
      }
    }
  }, [gptTaskList, gptList]);

  return (
    <>
      {renderGptList.map(
        ({
          key,
          rowClassName,
          pageCardClassName,
          title,
          state,
          paramsKey,
          ...props
        }) => {
          const promptValue: PromptConfirmItemType = promptInfoMap[key] || {};
          const { modelType } = promptValue;
          const { task_id: taskId, error_code } = gptTaskIdRef.current[key];

          let loading = !taskId && !error_code ? true : undefined;
          const loadingText = loading
            ? formatMessage({ id: 'Preparing the summary task' })
            : undefined;
          let isEmpty = !!error_code ? true : undefined;

          let emptyDesc = undefined;
          let realLeftElement = undefined;
          let realEmptyDescText = undefined;
          let hasEmptyDesc = false;
          let hasEmptyTitle = false;
          let hasEmptyImg = true;

          if (key === 'video') {
            const { loading: imgLoading, leftElement } = videoImgInfo;
            loading = loading || imgLoading ? true : undefined;
            realLeftElement = leftElement;
          }

          if (isEmpty) {
            // 修改筛选器后不自动请求
            if (error_code === '80000') {
              isEmpty = false;
            }
            else if (error_code === '30004') {
              emptyDesc = (
                <ul className="dbo-no-data-ul">
                  {emptyDescList.map((item, index) => {
                    return (
                      <li key={item}>
                        {index === 0 && (
                          <span className="dbo-no-data-ul-icon">
                            <Icon type="error" />
                          </span>
                        )}
                        {formatMessage({ id: item })}
                      </li>
                    );
                  })}
                  <li>
                    <Button
                      type="primary"
                      className="dbo-no-data-ul-btn"
                      onClick={() => toggleSummaryPrompt?.(key)}
                    >
                      <SvgIcon type="refresh" />
                      {formatMessage({ id: 'Regenerate AI Summary' })}
                    </Button>
                  </li>
                </ul>
              );
              hasEmptyImg = false;
            } else {
              hasEmptyDesc = true;
              hasEmptyTitle = true;
              if (key === 'video') {
                realEmptyDescText = 'This video has no subtitles';
              }
            }
          }

          // console.log(props, loading, '*---------------');

          return (
            <Row
              key={key}
              className={`dbo-analysis-card-container ${rowClassName} dbo-aigc-card-wrapper--${modelType}`}
            >
              <Col span={24}>
                <CardCommon className="dbo-aigc-card-wrapper">
                  <GPT.PageCard
                    {...props}
                    loadingHeight={220}
                    filter={filter}
                    contentCard
                    className={`gpt-summary ${pageCardClassName}`}
                    title={<div>{formatMessage({ id: title })}</div>}
                    fullTitle={<div>{formatMessage({ id: title })}</div>}
                    ref={(ref: any) => initRef?.(ref, key)}
                    hasStopBtn
                    alwaysShowTitle
                    showModelType={true}
                    oriFeedsState={state}
                    loadingElement={saveTipsElement}
                    needBasicTaskId
                    leftElement={realLeftElement}
                    backGPTText={(value: any) => {
                      if (value?.task_status === 'success') {
                        setApiLoading?.(`${key}gpt`);
                      }
                    }}
                    linkFilterChange={false}
                    promptValue={promptValue}
                    hasResultOutput={false}
                    promptConfirm={async (data: PromptConfirmItemType) => {
                      const flag = await setPromptInfo(data, key, paramsKey);
                      if (flag) {
                        gptTaskIdRef.current[key] = {
                          ...gptTaskIdRef.current[key],
                          task_id: data.taskId as string,
                        };
                      }
                    }}
                    loading={loading}
                    loadingText={loadingText}
                    needRequestPrompt={false}
                    empty={isEmpty}
                    hasEmptyDesc={hasEmptyDesc}
                    hasEmptyTitle={hasEmptyTitle}
                    hasEmptyImg={hasEmptyImg}
                    emptyDescElement={emptyDesc}
                    emptyDescText={realEmptyDescText}
                  >
                    {(toggleVisible: () => void, { loading, total }: any) => (
                      <GPT.Button
                        disabled={loading && total > 0}
                        onClick={() => toggleVisible()}
                      ></GPT.Button>
                    )}
                  </GPT.PageCard>
                </CardCommon>
              </Col>
            </Row>
          );
        },
      )}
    </>
  );
}
